import React, { FC } from 'react';
import classNames from 'classnames';
import capitalize from 'lodash.capitalize';
import { Cell } from 'react-table';
import { Link } from 'react-router-dom';
import { BADTable } from '@bad/components';
import { routes } from '../../../shared-interface';
import { LicensesType, LicenseUsageDto, LicenseWithStatsDto, UsageDto } from '../../../api/models/licenses';

import styles from './licenses-table.module.scss';
import { LicensesChart } from './licenses-chart';
import { isEmbedded } from '../../../common';

const countTotal = (data: UsageDto[], propName: 'available' | 'used'): number => {
  let result = 0;
  data.forEach((el) => (result = result + el[propName]));
  return result;
};

export const countGlobal = (data: LicenseWithStatsDto[], propName: 'available' | 'used') => {
  let result = 0;
  data.forEach(({ usage }) => {
    // @ts-ignore
    const stats: UsageDto[] = [usage.regular, usage.trueUp, usage.virtual].filter(Boolean);
    result += countTotal(stats, propName);
  });
  return result;
};

export const LicensesTable: FC<{
  licensesType: LicensesType;
  data: LicenseWithStatsDto[];
  className?: string;
}> = ({ licensesType, data, className }) => {
  const numberCellClassName = (el: number, isBold: boolean = false, isRatio: boolean = false) =>
    classNames([styles.numberCell, isBold && 'wt-h3', isRatio && el === 100 && styles.fullNumberCell, el === 0 && styles.emptyNumberCell]);

  const renderNumberCell = (data: UsageDto[], propName: 'available' | 'used' | 'ratio') =>
    data.map((el, i) => {
      let value = propName === 'ratio' ? Math.round((el.used / el.available) * 100) ?? 0 : el[propName];
      if (propName === 'ratio' && (value === 100 || value === 0)) {
        if (value === 0 && el.used !== 0) value = 1;
        if (value === 100 && el.used !== el.available) value = 99;
      }
      const isRatio = propName === 'ratio';
      if (isNaN(value) || !isFinite(value)) value = 0;
      return (
        <div key={JSON.stringify([value, i])} className={classNames(numberCellClassName(value, data.length === 1, isRatio), styles.versionsCell)}>
          {value}
          {isRatio && '%'}
        </div>
      );
    });

  let columns = [
    {
      id: 'licensesInfo',
      Header: capitalize(licensesType),
      minWidth: 28,
      Cell: (value: Cell<LicenseWithStatsDto>) => {
        return (
          <div>
            <Link to={routes().admin.licenses.tabs.versions({ licenseId: value.row.original.id, licensesType })} className={`wt-link ${styles.licensesInfoLink}`}>
              {value.row.original.name}
            </Link>
          </div>
        );
      },
    },
    {
      id: 'type',
      accessor: 'usage',
      Header: 'License type',
      minWidth: 15,
      Cell: ({ value }: Cell<LicenseUsageDto>) => {
        const stats = [
          { name: 'Regular', ...value.regular },
          { name: 'True-up', ...value.trueUp },
          { name: 'Virtual', ...value.virtual },
        ].filter((el) => Object.keys(el).includes('available'));

        return stats.length === 1 ? (
          <div className={classNames('wt-h3', styles.h3)}>{stats[0].name}</div>
        ) : (
          <>
            <div className={classNames('wt-h3', styles.h3)}>All</div>
            {stats.map((el, i) => (
              <div key={JSON.stringify([el.name, i])} className={styles.versionsCell}>
                {el.name}
              </div>
            ))}
          </>
        );
      },
    },
    {
      id: 'addedLicenses',
      accessor: 'usage',
      Header: <div className={styles.numberCell}>Added licenses</div>,
      minWidth: 12,
      Cell: ({ value }: Cell<LicenseUsageDto>) => {
        const stats = [value.regular, value.trueUp, value.virtual].filter(Boolean);
        const total = countTotal(stats, 'available');
        return (
          <>
            {(stats.length > 1 || stats.length === 0) && <div className={numberCellClassName(total, true)}>{total}</div>}
            {renderNumberCell(stats, 'available')}
          </>
        );
      },
    },
    {
      id: 'usedLicenses',
      accessor: 'usage',
      Header: <div className={styles.numberCell}>Used licenses</div>,
      minWidth: 11,
      Cell: ({ value }: Cell<LicenseUsageDto>) => {
        const stats = [value.regular, value.trueUp, value.virtual].filter(Boolean);
        const total = countTotal(stats, 'used');
        return (
          <>
            {(stats.length > 1 || stats.length === 0) && <div className={numberCellClassName(total, true)}>{total}</div>}
            {renderNumberCell(stats, 'used')}
          </>
        );
      },
    },
    {
      id: 'usageRatio',
      accessor: 'usage',
      Header: <div className={styles.numberCell}>Usage ratio</div>,
      minWidth: 10,
      Cell: ({ value }: Cell<LicenseUsageDto>) => {
        const stats = [value.regular, value.trueUp, value.virtual].filter(Boolean);
        let total = Math.round((countTotal(stats, 'used') / countTotal(stats, 'available')) * 100);
        if (isNaN(total) || !isFinite(total)) total = 0;
        return (
          <>
            {(stats.length > 1 || stats.length === 0) && <div className={numberCellClassName(total, true)}>{total}%</div>}
            {renderNumberCell(stats, 'ratio')}
          </>
        );
      },
    },
  ];
  if (!isEmbedded()) {
    columns.push({
      id: 'chart',
      accessor: 'id',
      Header: 'Allocation in the last 60 days',
      minWidth: 20,
      Cell: ({ value }: Cell<LicenseUsageDto>) => <LicensesChart licenseId={value} />,
    });
  }
  // @ts-ignore
  return <BADTable className={classNames(className, 'wt-text-3', styles.table)} offsetTableSize="s" wide={true} tableData={data} columnsFn={() => columns} />;
};
